import type { ArtifactContent, VideoSource } from "../ChatEventTypes";
import type { MentionedVideoItem } from "../components/VideoMention";

/**
 * A clip out of a `video_sources` artifact, in the shape a `video_mention`
 * event carries it. The two describe the same thing — a range of a video, with
 * its owner and presigned URLs — so folding sources onto the mention shape lets
 * everything downstream (the question box's Videos tab, `getMentions`) treat a
 * source and a citation alike.
 *
 * `index` is the one field with no counterpart: it numbers a citation's marker
 * in the answer's text, and a source is never cited inline.
 */
export type VideoSourceMention = Omit<MentionedVideoItem, "index">;

/**
 * Reads one source as a mention. A source has no mention id of its own — the
 * `vm_` NanoID on a `video_mention` is minted per event — so the video's public
 * id stands in, which is what identifies the clip everywhere it matters (see
 * mentionCollector's `videoKey`).
 */
const videoSourceMention = (
	source: VideoSource,
	videoId: string,
): VideoSourceMention => ({
	id: videoId,
	start_time_sec: source.start_time ?? null,
	end_time_sec: source.end_time ?? null,
	thumbnail_url: source.thumbnail_url,
	aspect_ratio: source.aspect_ratio,
	title: source.content_summary || source.title || null,
	video_id: videoId,
	video_url: source.video_url,
	video_duration: source.video_duration,
	hls_ready: source.hls_ready,
	influencer: source.influencer,
	social_account: source.social_account,
	view_count: source.view_count,
	post_url: source.post_url,
});

/**
 * The clips a hydrated `video_sources` artifact carries, as mentions; nothing
 * for an artifact of any other type. A source that hydration could not resolve
 * to a video is dropped: with no video id there is no clip to offer or play.
 */
export function artifactVideoSourceMentions(
	artifact: ArtifactContent | undefined,
): VideoSourceMention[] {
	if (artifact?.type !== "video_sources") {
		return [];
	}
	const sources = artifact.artifact?.sources;
	if (!Array.isArray(sources)) {
		return [];
	}
	const mentions: VideoSourceMention[] = [];
	for (const source of sources) {
		const videoId = source?.video_id;
		if (typeof videoId !== "string" || videoId.length === 0) {
			continue;
		}
		mentions.push(videoSourceMention(source, videoId));
	}
	return mentions;
}
